import type { Catalog, Liturgy, MassRecord, ValidationInfo, ValidationStatus } from "./types";
import { suggestSongs, defaultOrdinary } from "./lib/songs";

export interface SongChoice {
  title: string;
  annotation: string; // "feuille" | "chorale seule" | ""
  page: string; // page / numéro dans le carnet (libre), ex. "12"
  reason?: string; // why the AI suggested this song (shown under the field, persisted)
}

export interface DocState {
  date: string;
  liturgy: Liturgy;

  motAccueil: string;
  avant1: string;
  avant2: string;
  psalmRefrain: string; // refrain du psaume (préfill AELF, éditable)
  prierePuIntro: string;
  prierePuRefrain: string;
  prierePuIntentions: string; // one intention per line

  entree: SongChoice;
  offertoire: SongChoice;
  communion: SongChoice;
  envoi: SongChoice;
  ordinary: string;

  oraisonOuverture: string;
  oraisonOffertoire: string;
  oraisonCommunion: string;

  includeReadingsText: boolean;

  team?: string; // id of the animation team responsible for this mass
  validation?: ValidationInfo; // priest sign-off workflow (absent ⇒ "en_cours")
}

// Kept in sync with the first entry of PU_REFRAINS (IntentionRefrainPicker) so a
// fresh draft's refrain is pre-selected in the list rather than shown as "Autre".
const DEFAULT_REFRAIN = "Accueille au creux de tes mains la prière de tes enfants.";

const DEFAULT_INTENTIONS = [
  "Pour l'Église : qu'elle annonce l'Évangile avec fidélité et reste attentive aux plus fragiles. Prions le Seigneur.",
  "Pour les responsables des nations : qu'ils recherchent la justice, la paix et le bien commun. Prions le Seigneur.",
  "Pour les personnes éprouvées par la maladie, la solitude ou le deuil : qu'elles trouvent réconfort et espérance. Prions le Seigneur.",
  "Pour notre communauté : que chacun y trouve sa place dans la prière, le service et la fraternité. Prions le Seigneur.",
].join("\n");

function pick(
  catalog: Catalog,
  moment: string,
  season: Liturgy["seasonKey"],
  taken: Set<string>
): SongChoice {
  const ranked = suggestSongs(catalog, moment, season).filter((r) => r.score > 1);
  const s = ranked.find((r) => !taken.has(r.song.title))?.song;
  const annotation = s?.annotations?.[0] || (moment === "offertoire" ? "chorale seule" : "feuille");
  if (s) taken.add(s.title);
  return { title: s?.title || "", annotation, page: "" };
}

export function initDocState(liturgy: Liturgy, catalog: Catalog): DocState {
  const o = liturgy.oraisons;
  const taken = new Set<string>();
  return {
    date: liturgy.date,
    liturgy,
    motAccueil: "",
    avant1: "",
    avant2: "",
    psalmRefrain: liturgy.psalm?.refrain || "",
    prierePuIntro: "",
    prierePuRefrain: DEFAULT_REFRAIN,
    prierePuIntentions: DEFAULT_INTENTIONS,
    entree: pick(catalog, "entree", liturgy.seasonKey, taken),
    offertoire: pick(catalog, "offertoire", liturgy.seasonKey, taken),
    communion: pick(catalog, "communion", liturgy.seasonKey, taken),
    envoi: pick(catalog, "envoi", liturgy.seasonKey, taken),
    ordinary: defaultOrdinary(catalog),
    oraisonOuverture: o?.ouverture || "",
    oraisonOffertoire: o?.offertoire || "",
    oraisonCommunion: o?.communion || "",
    includeReadingsText: false,
    validation: { status: "en_cours" },
  };
}

/** The validation state of a draft, defaulting to "en_cours" for older docs. */
export function validationOf(doc: DocState): ValidationInfo {
  return doc.validation ?? { status: "en_cours" };
}

/** Human label for a validation status. */
export function validationLabel(status: ValidationStatus): string {
  return status === "valide" ? "Validé par le Père" : status === "a_valider" ? "À valider par le Père" : "En préparation";
}

/** Gloria is omitted during Lent and Advent (but kept for feasts/solemnities,
 *  which AELF returns with no season → "autre"). */
export function hasGloria(season: Liturgy["seasonKey"]): boolean {
  return season !== "careme" && season !== "avent";
}

/** The Alleluia is dropped only in Lent (Advent keeps it). */
export function allowsAlleluia(season: Liturgy["seasonKey"]): boolean {
  return season !== "careme";
}

/** A short reminder of the season's liturgical constraints, or null. */
export function liturgicalNote(season: Liturgy["seasonKey"]): string | null {
  if (season === "careme")
    return "Carême : pas de Gloria ni d'Alléluia — l'acclamation prend sa forme propre au Carême (« Gloire à toi, Seigneur… »). Privilégiez des chants sobres.";
  if (season === "avent") return "Avent : pas de Gloria (l'Alléluia reste permis). Chants dans l'esprit de l'attente.";
  return null;
}

/**
 * Build a DocState for a PAST mass we already prepared (an archive): the actual
 * songs we sang + whatever document text we extracted from the original .docx,
 * over the day's real readings (AELF).
 */
export function initArchiveDocState(rec: MassRecord, liturgy: Liturgy, catalog: Catalog): DocState {
  const d = rec.doc || {};
  const o = liturgy.oraisons;
  const song = (moment: string, title?: string): SongChoice => {
    if (!title) return { title: "", annotation: moment === "offertoire" ? "chorale seule" : "feuille", page: "" };
    const s = catalog.songs.find((x) => x.title === title || x.aliases.includes(title));
    return { title: s?.title || title, annotation: s?.annotations?.[0] || (moment === "offertoire" ? "chorale seule" : "feuille"), page: "" };
  };
  return {
    date: liturgy.date,
    liturgy,
    motAccueil: d.motAccueil || "",
    avant1: d.avant1 || "",
    avant2: d.avant2 || "",
    psalmRefrain: liturgy.psalm?.refrain || "",
    prierePuIntro: d.prierePuIntro || "",
    prierePuRefrain: d.prierePuRefrain || DEFAULT_REFRAIN,
    prierePuIntentions: d.prierePuIntentions || DEFAULT_INTENTIONS,
    entree: song("entree", rec.songs.entree),
    offertoire: song("offertoire", rec.songs.offertoire),
    communion: song("communion", rec.songs.communion),
    envoi: song("envoi", rec.songs.envoi),
    ordinary: rec.ordinary || defaultOrdinary(catalog),
    oraisonOuverture: d.oraisonOuverture || o?.ouverture || "",
    oraisonOffertoire: d.oraisonOffertoire || o?.offertoire || "",
    oraisonCommunion: d.oraisonCommunion || o?.communion || "",
    includeReadingsText: false,
  };
}
